import React from "react";
import './chatPage.css'
import './sidebar.css'
import './genericChatButton.css'
import CryptoJS from "crypto-js";
import LoadingChatIcon from "./chatPageComponents/LoadingChatIcon";
import ChatBoxText from "./chatPageComponents/ChatBoxText";
import ChatBoxDataTable from "./chatPageComponents/ChatBoxDataTable/ChatBoxDataTable";
import ChatBoxGenericSuggestions from "./chatPageComponents/sidebar/sidebarGenericSuggestions/sidebarGenericSuggestions";
import { queryAutosuggest } from "./chatPageUtil/queryAutosuggest";
import { queryChat } from "./chatPageUtil/queryChat";
import AbortController from "abort-controller";
import { Tooltip } from "@material-ui/core";
import LinearProgress from '@material-ui/core/LinearProgress';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import ChatPageSettings from "./chatPageComponents/sidebar/ChatPageSettings/chatPageSettings";
import ChatBoxOrionHello from "./chatPageComponents/ChatBoxOrionHello/ChatBoxOrionHello";
import { mainTheme } from "../../theme/ddTheme";
import { queryQuestionEnhacement } from "./chatPageUtil/queryQuestionEnhacement";
import { queryDashboardLinks } from "./chatPageUtil/queryDashboardLinks";
import { fitTextArea } from "./chatPageUtil/fitTextArea";
import { startChatQuery } from "./chatPageUtil/startChatQuery";

// Default Chat Config
const defaultSelections = "customer operations sales"
const defaultChat = []
const defaultChatLength = 0

const theme = createMuiTheme(mainTheme);

class ChatPage extends React.Component {
  constructor(props) {
    super(props);

    const clientName = JSON.parse(
      CryptoJS.AES.decrypt(
        localStorage.getItem("conceptName"),
        process.env.REACT_APP_PASS
      ).toString(CryptoJS.enc.Utf8)
    )
    this.controller = new AbortController()
    this.state = {
      clientName: clientName,
      // Basic Chat states
      messages: [],
      inputText: "",
      currentEditIndex: -1,
      currentEditText: "",
      isChatLoading: false,
      keywords: [],
      hideSidebar: false,
      genericChatButtonToggleState: new Set(),
      // Enhance Question States
      isEnhacingQuestion: false,

      // Settings States
      showSettings: false,
      fontSize: 18,
      assistantChatBgColor: "white",
      userChatBgColor: "silver",
      toggleAutoScroll: false,
    };
  }

  // Use Effect?
  componentDidMount() {
    const asyncFunc = async () => {
      let data = await queryAutosuggest(defaultSelections, this.state.clientName)
      this.setState({ keywords: data })
    }
    asyncFunc()
  }

  copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Text copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  }

  // Handle Fetch Sugggested Dashboards
  async handleSuggestedDashboard(newInput) {
    // Check if input not empty
    if (newInput === "") {
      return
    }

    // Query For New Dashboard
    let newResponse = await queryDashboardLinks(newInput, this.state.clientName, this.controller.signal)

    // Check if response was aborted
    if(newResponse.hasOwnProperty("isAborted")) {
      return
    }

    // Update Front end States
    this.setState(e => {
      e.messages.push({
        role: 'dashboard_links',
        reports: newResponse.reports,
        message: newResponse.message
      })
      return e
    })
  }

  async handleChatBotPrompt(newInput, messages) {
    // Check if input not empty
    if (newInput === "") {
      return
    }

    // Start Querying Chat GPT
    startChatQuery(
      messages,
      newInput,
      this.state.clientName,
      this.controller.signal
    )
    
    // Wait for Response
    let newMessages = await queryChat(
      messages,
      newInput,
      this.state.clientName,
      this.controller.signal
    )

    // Append New Message to State
    this.setState(e => {
      for (let item of newMessages) {
        e.messages.push(item)
      }
      e.isChatLoading = false
      return e
    })
    this.handleScroll()
  }

  // Handle Send
  async handleSend(newInput) {
    // Prep Font End States
    this.setState(e => {
      e.messages.push({
        role: "user",
        content: newInput
      })
      e.inputText = ""
      e.isChatLoading = true
      return e
    })
    this.handleScroll()

    await Promise.all([
      // Wait for ChatBot to Respond
      this.handleChatBotPrompt(newInput, this.state.messages),
      // Wait for Dashboard Suggestions
      this.handleSuggestedDashboard(newInput)
    ])
  }

  // Handle Edit Set to Send Data
  async handleEditOnLeave(idx) {
    this.setState(e => e.currentEditIndex = -1)
    if (this.state.messages[idx].content !== this.state.currentEditText && this.state.currentEditText !== "") {
      // Prep font end state
      this.setState(e => {
        // Reset Edit Chat Box
        e.messages[idx].content = this.state.currentEditText
        // Update Loading Chat
        e.isChatLoading = true
        e.messages = e.messages.slice(0, idx + 1)
        return e
      })
      this.handleScroll()

      await Promise.all([
        // Wait for ChatBot to Respond
        this.handleChatBotPrompt(this.state.currentEditText, this.state.messages.slice(0, idx + 1)),
        // Wait for Dashboard Suggestions
        this.handleSuggestedDashboard()
      ])
    }
  }

  // Handle Edit
  handleEdit(idx, content) {
    this.setState(e => {
      e.currentEditIndex = idx
      e.currentEditText = content
      return e
    })
  }

  // Handle Scroll
  handleScroll() {
    if (this.state.toggleAutoScroll) {
      const element = document.getElementById('bottom-ref');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  async handlePromptEnhancement() {
    // Do not perfom update if query is fetching
    if (this.state.isChatLoading || this.state.inputText === '' || this.state.isEnhacingQuestion) {
      return
    }

    // Update front end state so the question is querying
    this.setState(e => {
      e.isEnhacingQuestion = true
      return e
    })

    // Query for Upgraded Input Text
    let newInputText = await queryQuestionEnhacement(this.state.inputText, this.state.clientName)

    // Cleanout current inputText
    this.setState(e => {
      e.inputText = ""
      return e
    })

    // Type Answer New Input Text over 1 second
    let sleepTime = Math.floor(1000 / newInputText.length)
    for (let char of newInputText) {
      this.setState(e => {
        e.inputText += char
        return e
      })
      fitTextArea()
      await new Promise(r => setTimeout(r, sleepTime));
    }

    // Update the front end to render new chatbox
    this.setState(e => {
      e.isEnhacingQuestion = false
      return e
    })
  }

  render() {
    return (
      <MuiThemeProvider theme={theme}>
        <div className="clean-page">
          {/* Main Page Content */}
          <main className="content">
            <div style={{ position: "relative", height: '100vh', overflow: 'hidden', }}>
              <div style={{ paddingTop: '2vh', paddingLeft: '1vw', paddingRight: '1vw', paddingBottom: '20vh', overflow: 'scroll', height: '100vh' }}>
                {/* Generic Chat */}
                <ChatBoxOrionHello assistantChatBgColor={this.state.assistantChatBgColor} fontSize={this.state.fontSize} />
                {this.state.messages.map((obj, idx) => {
                  if (obj.role === 'user') {
                    let textAreaLines = this.state.currentEditText.split("\n")
                    const textAreaRows = textAreaLines.length
                    let maxLength = textAreaLines.reduce((max, str) => Math.max(max, str.length), 0)
                    if (maxLength > 100) {
                      maxLength = 100
                    }
                    return (
                      <div style={{ display: 'inline-block', width: '100%' }}>
                        {idx === this.state.currentEditIndex ? (
                          <div key={idx + obj.content} style={{ display: 'flex', float: 'right' }}>
                            <div className="paper-template" style={{ backgroundColor: this.state.userChatBgColor }} >
                              <textarea
                                rows={textAreaRows}
                                cols={maxLength}
                                style={{ maxWidth: 600, fontSize: this.state.fontSize, minHeight: 40, backgroundColor: this.state.userChatBgColor }}
                                autoFocus
                                value={this.state.currentEditText}
                                onChange={(e) => { this.setState({ currentEditText: e.target.value }) }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey && this.state.currentEditText !== "") {
                                    this.handleEditOnLeave(idx)
                                  }
                                }}
                                onBlur={() => {
                                  this.handleEditOnLeave(idx)
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div key={idx + obj.content} style={{ display: 'flex', float: 'right' }}>
                            <Tooltip title="Edit">
                              <button disabled={this.state.isChatLoading} style={{ marginRight: 10, height: 50, width: 50, color: "black" }} className="chat-func-button" onClick={() => { this.handleEdit(idx, obj.content) }}>
                                <img src={"/chat/edit.svg"} />
                              </button>
                            </Tooltip>
                            <div className="paper-template" style={{ backgroundColor: this.state.userChatBgColor }} >
                              <ChatBoxText fontSize={this.state.fontSize} content={obj.content} />
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  }
                  else if (obj.role === 'assistant') {
                    return (
                      <React.Fragment>
                        {/* Asssistant Chat */}
                        <div style={{ display: 'inline-block', width: '100%' }}>
                          <div key={idx + obj.content} style={{ display: 'flex', float: 'left' }}>
                            <img className="chat-avatar" src={"/chat/Orion.svg"} style={{ height: 50, width: 50, borderRadius: 20, marginRight: 5, marginTop: 0 }} />
                            <div className="paper-template" style={{ backgroundColor: this.state.assistantChatBgColor }}>
                              <ChatBoxText fontSize={this.state.fontSize} content={obj.content} />
                            </div>
                            <Tooltip title="Copy Text">
                              <button
                                style={{ marginLeft: 10, height: 50, width: 50 }}
                                className="chat-func-button"
                                onClick={() => this.copyToClipboard(obj.content)}
                              >
                                <img src={"/chat/copy.svg"} />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                        {/* Toggle Bonus Content */}
                        <div style={{ display: 'inline-block', width: '100%', marginBottom: '1vh' }}>
                          <div style={{ display: 'flex', float: 'left', paddingLeft: 40, width: "100%" }}>
                            {obj.hasOwnProperty("bonus_content") && obj.bonus_content.map((innerContent) => {
                              const contentTitle = innerContent.role.charAt(0).toUpperCase() + innerContent.role.slice(1)
                              return (
                                <button
                                  style={{
                                    backgroundColor: this.state.genericChatButtonToggleState.has(obj.uuid + innerContent.role) ? "var(--data-delivers-green)" : "transparent",
                                    padding: 10
                                  }}
                                  className="generic-chat-button"
                                  onClick={() => {
                                    // Toggle Button State
                                    if (this.state.genericChatButtonToggleState.has(obj.uuid + innerContent.role)) {
                                      this.setState(e => e.genericChatButtonToggleState.delete(obj.uuid + innerContent.role))
                                    }
                                    else {
                                      this.setState(e => e.genericChatButtonToggleState.add(obj.uuid + innerContent.role))
                                    }
                                  }}>
                                  <img src={`/chat/genericChat/${innerContent.role}.svg`} />
                                  <br />
                                  {contentTitle}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                        {obj.hasOwnProperty("bonus_content") && obj.bonus_content.map((innerContent) => {
                          if (innerContent.role === "visualization" && this.state.genericChatButtonToggleState.has(obj.uuid + innerContent.role)) {
                            return (
                              <div key={innerContent.content} style={{ display: 'inline-block', width: '100%', marginBottom: '1vh' }}>
                                <iframe
                                  className="vis-frame"
                                  src={innerContent.content}
                                  style={{ height: 500, width: '100%', padding: 0, borderRadius: 5 }}
                                />
                              </div>
                            )
                          }
                          else if (innerContent.role === "data" && this.state.genericChatButtonToggleState.has(obj.uuid + innerContent.role)) {
                            return (
                              <div key={innerContent.content} style={{ display: 'inline-block', width: '100%', marginBottom: '1vh' }}>
                                <ChatBoxDataTable data={innerContent.content} />
                              </div>
                            )
                          }
                        })}
                      </React.Fragment>
                    )
                  }
                  else if (obj.role === 'dashboard_links' && obj.reports.length > 0) {
                    return (
                      <div style={{ display: 'inline-block', width: '100%' }}>
                        <div key={idx + obj.content} style={{ display: 'flex', float: 'left' }}>
                          <img className="chat-avatar" src={"/chat/Orion.svg"} style={{ height: 50, width: 50, borderRadius: 20, marginRight: 5, marginTop: 0 }} />
                          <div className="paper-template" style={{ backgroundColor: this.state.assistantChatBgColor }}>
                            <ChatBoxText fontSize={this.state.fontSize} content={obj.message} />
                            <ul style={{ fontSize: this.state.fontSize }}>
                              {obj.reports.map(report => {
                                return (
                                  <li>
                                    <div style={{ display: 'flex' }}>
                                      <div style={{ width: 15 }}>
                                        <b>•</b>
                                      </div>
                                      <div>
                                        <u>
                                          <a onClick={()=>{window.open(`${window.location.origin}${report.path}`, '_blank');}}>{report.name}</a>
                                        </u>
                                      </div>
                                    </div>
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )
                  }
                })}
                {/* Loading Component */}
                {this.state.isChatLoading ? (
                  <div style={{ display: 'inline-block', width: '100%' }}>
                    <div style={{ display: 'flex', float: 'left' }}>
                      <img src={"/chat/Orion.svg"} style={{ height: 50, width: 50, borderRadius: 20, marginRight: 5, marginTop: 0 }} />
                      <div className="paper-template" style={{ backgroundColor: this.state.assistantChatBgColor }}>
                        <LoadingChatIcon />
                      </div>
                    </div>
                  </div>
                ) : null}
                {/* Scroll To Bottom */}
                <div id="bottom-ref" />
              </div>
              {/* User Input Area */}
              <div className="user-input" style={{ height: '15%', marginBottom: '-1vh' }}>
                <div className="user-input-container">
                  <textarea
                    id="orion-chat-text-area"
                    style={{ 
                      fontSize: this.state.fontSize, 
                      paddingRight: '8vw', 
                      paddingLeft: '1.5vw',
                      position:'absolute',
                      bottom: '2.25vh',
                      left:5,
                    }}
                    onInput={()=>{fitTextArea()}}
                    disabled={this.state.isChatLoading || this.state.isEnhacingQuestion}
                    className="user-text-area"
                    placeholder={this.state.isChatLoading ? "Please wait, while your results are curated!" : "Type your questions."}
                    value={this.state.inputText}
                    onKeyPress={(e) => {
                      // Handle Enter Key
                      if (e.key === 'Enter' && !e.shiftKey && this.state.inputText !== "") {
                        this.handleSend(this.state.inputText)
                      }
                      // Handle Space Key for Auto Suggestions
                      // if (this.state.inputText !== "" && e.key === ' ') {
                      //   const asyncFunc = async () => {
                      //     let data = await queryAutosuggest(this.state.inputText + defaultSelections, this.state.clientName)
                      //     this.setState({
                      //       keywords: data,
                      //       inputTextWordLength: this.state.inputText.split(' ').length
                      //     })
                      //   }
                      //   asyncFunc()
                      // }
                    }}
                    onChange={(e) => { this.setState({ inputText: e.target.value }) }} />
                  {/* Send, Cancel, and Reset Button */}
                  <button className="send-button"
                    style={{ 
                      position:'absolute',
                      bottom: '2.25vh',
                      right:'1vw',
                      padding:'0.5%'
                    }}
                    onClick={() => {
                    // Handle Send
                    if (!this.state.isChatLoading && this.state.inputText !== "") {
                      this.handleSend(this.state.inputText)
                    }
                    // Handle Reset
                    else if (!this.state.isChatLoading && this.state.messages.length !== defaultChatLength) {
                      if (window.confirm("Are you sure you want to reset chat?")) {
                        this.setState(e => e.messages = defaultChat.slice(0, defaultChatLength))
                      }
                    }
                    // Handle Cancel
                    else {
                      this.controller.abort()
                      this.controller = new AbortController()
                    }
                  }}>
                    {!this.state.isChatLoading ? (
                      <React.Fragment>
                        {this.state.inputText !== "" || this.state.messages.length === defaultChatLength ? (
                          <React.Fragment>
                            Ask AI
                            <br />
                            <img src={!this.state.isChatLoading ? '/chat/send.svg' : '/chat/cancel.svg'} />
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            Reset Chat
                            <br />
                            <img src={!this.state.isChatLoading ? '/chat/reset.svg' : '/chat/cancel.svg'} />
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        Cancel
                        <br />
                        <img src={!this.state.isChatLoading ? '/chat/send.svg' : '/chat/cancel.svg'} />
                      </React.Fragment>
                    )}
                  </button>
                  {/* Enhance Question Button */}
                  <div style={{
                    position: 'absolute',
                    bottom: this.state.hideSidebar ? '8.25%' : '15%',
                    left: this.state.hideSidebar ? '73.5%' : '73%',
                    width: '12%',
                    padding: '1%'
                  }}
                  >
                    <Tooltip title="Enhance your question using AI">
                      <button style={{ height: '8vh', width: '100%', background: 'transparent' }} className="send-button" onClick={() => { this.handlePromptEnhancement() }}>
                        Enhance
                        <br />
                        {this.state.isEnhacingQuestion ? (
                          <LinearProgress />
                        ) : (
                          <img src={'/chat/improve-prompt-icon.svg'} />
                        )}
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </main>
          {/* Side Bar Content */}
          {!this.state.hideSidebar ? (
            <aside className="sidebar">
              <div className="inner-sidebar">
                {/* Close Button & Return To Menu */}
                <Tooltip title={!this.state.showSettings ? "Hide Sidebar" : "Return to Menu"}>
                  <button className="sidebar-button" onClick={() => {
                    if (!this.state.showSettings) {
                      this.setState({ hideSidebar: true })
                    }
                    else {
                      this.setState({ showSettings: false })
                    }
                  }}>
                    <img src={"/chat/right-arrow.svg"} />
                  </button>
                </Tooltip>
                {/* Settings Button */}
                {!this.state.showSettings ? (
                  <div style={{ position: "fixed", top: 1, right: 0 }}>
                    {/* Show Settings Section */}
                    <Tooltip title="Settings">
                      <button style={{ width: 50, paddingRight: 0 }} className="sidebar-button" onClick={() => { this.setState({ showSettings: true }) }}>
                        <img src={"/chat/settings.svg"} />
                      </button>
                    </Tooltip>
                  </div>
                ) : null}
                <div style={{ padding: '1%' }}>
                  {this.state.showSettings ? (
                    // Settings Section
                    <ChatPageSettings
                      fontSize={this.state.fontSize}
                      toggleAutoScroll={this.state.toggleAutoScroll}
                      onFontChange={(e) => { this.setState({ fontSize: e }) }}
                      onAssistantChatBgColorChange={(e) => { this.setState({ assistantChatBgColor: e }) }}
                      onUserChatBgColorChange={(e) => { this.setState({ userChatBgColor: e }) }}
                      onToggleAutoScrollChange={() => {
                        console.log("CLICKED")
                        this.setState({ toggleAutoScroll: !this.state.toggleAutoScroll })
                      }}
                    />
                  ) : (
                    // Auto Suggestions
                    <ChatBoxGenericSuggestions
                      isChatLoading={this.state.isChatLoading}
                      data={this.state.keywords}
                      setMessage={(i) => { this.handleSend(i) }}
                    />
                  )}
                </div>
              </div>
            </aside>
          ) : (
            <div style={{ position: "fixed", top: 10, right: 10 }}>
              {/* Show Side Bar */}
              <Tooltip title="Menu">
                <button style={{ minWidth: 20 }} className="sidebar-button" onClick={() => { this.setState({ hideSidebar: false }) }}>
                  <img src={"/chat/menu.svg"} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </MuiThemeProvider>
    );
  }
}

export default ChatPage;



// WEBPACK FOOTER //
// ./src/views/ChatPage/ChatPage.jsx